import React from "react";
import {Card, Descriptions, Divider, Space, Typography} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useTranslate} from "../../../hooks/translate.hook";
import {HealthCheckerIssueDetails} from "../../../models/health-checker/health-checker";
import {formatHealthCenterDateTime} from "../health-center.helpers";

const {Paragraph, Text} = Typography;

interface IssueDetailsProps {
    issue: HealthCheckerIssueDetails;
}

export const IssueDetails: React.FC<IssueDetailsProps> = ({issue}) => {
    const t = useTranslate();

    return (
        <Card
            className="health-center-issue__card health-center-issue__details"
            title={(
                <Space>
                    <FontAwesomeIcon icon={["fas", "heart-pulse"]} />
                    {t("HEALTH-CENTER.ISSUE.DETAILS", {defaultValue: "Issue and check details"})}
                </Space>
            )}
        >
            <div className="health-center-issue__description">
                <Text type="secondary">
                    {t("HEALTH-CENTER.ISSUE.DESCRIPTION", {defaultValue: "Description"})}
                </Text>
                <Paragraph>{issue.description}</Paragraph>
            </div>

            <Descriptions
                className="health-center-issue__descriptions"
                column={{xs: 1, sm: 2}}
                colon={false}
                size="small"
            >
                <Descriptions.Item label={t("HEALTH-CENTER.ISSUE.CHECK", {defaultValue: "Check"})}>
                    <Space size={6} wrap>
                        <Text strong>{issue.check.checkerName}</Text>
                        <Text type="secondary">#{issue.check.id}</Text>
                    </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t("HEALTH-CENTER.ISSUE.HOURS", {defaultValue: "Hours"})}>
                    {t("HEALTH-CENTER.DASHBOARD.HOURS", {
                        hours: issue.check.hours.join(", "),
                        defaultValue: "{{hours}}",
                    })}
                </Descriptions.Item>
                <Descriptions.Item label={t("HEALTH-CENTER.ISSUE.LAST-CHECKED", {defaultValue: "Last checked"})}>
                    {formatHealthCenterDateTime(issue.check.lastCheckedAt)}
                </Descriptions.Item>
                <Descriptions.Item label={t("HEALTH-CENTER.ISSUE.NEXT-CHECK", {defaultValue: "Next check"})}>
                    <Text className="health-center-issue__next-check">
                        <FontAwesomeIcon icon={["fas", "clock"]} /> {formatHealthCenterDateTime(issue.check.nextCheckAt)}
                    </Text>
                </Descriptions.Item>
                <Descriptions.Item label={t("HEALTH-CENTER.ISSUE.CHECK-CREATED", {defaultValue: "Check created"})}>
                    {formatHealthCenterDateTime(issue.check.createdAt)}
                </Descriptions.Item>
            </Descriptions>

            <Divider />
            <div className="health-center-issue__comment">
                <Text strong>{t("HEALTH-CENTER.ISSUE.COMMENT", {defaultValue: "Comment"})}</Text>
                <Paragraph type={issue.comment ? undefined : "secondary"}>
                    {issue.comment || t("HEALTH-CENTER.ISSUE.NO-COMMENT", {defaultValue: "No comment"})}
                </Paragraph>
            </div>
        </Card>
    );
};
